import { useEffect, useRef, useState } from "react";

/**
 * A newer build of an installable app (EP10-S23, the Store Portal and the
 * Driver App).
 *
 * The service worker is registered in prompt mode (`apps/pwa.ts`), so a new
 * deploy installs beside the running one and waits. This asks for an update
 * whenever `checkKey` changes — the route, so a deploy shows within one
 * navigation — and reports when one is waiting.
 */
export function useServiceWorkerUpdate(checkKey?: unknown): {
	available: boolean;
	/** Moves onto the new build: the waiting worker takes over, then the page reloads. */
	apply: () => void;
} {
	const [waiting, setWaiting] = useState<ServiceWorker | null>(null);
	const registration = useRef<ServiceWorkerRegistration | null>(null);

	useEffect(() => {
		const container = navigator.serviceWorker;
		if (container === undefined) {
			return;
		}
		let live = true;
		void container.getRegistration().then((r) => {
			if (!live || r === undefined) {
				return;
			}
			registration.current = r;
			const watch = () => {
				if (live && r.waiting !== null) {
					setWaiting(r.waiting);
				}
			};
			watch();
			r.addEventListener("updatefound", () => r.installing?.addEventListener("statechange", watch));
		});
		return () => {
			live = false;
		};
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: re-checked on every navigation, by design
	useEffect(() => {
		void registration.current?.update().catch(() => undefined);
	}, [checkKey]);

	return { available: waiting !== null, apply: () => applyUpdate(waiting) };
}

/** Hands over to the waiting worker and reloads onto it; a plain reload when none waits. */
export function applyUpdate(waiting: ServiceWorker | null): void {
	if (waiting === null) {
		window.location.reload();
		return;
	}
	navigator.serviceWorker.addEventListener("controllerchange", () => window.location.reload(), {
		once: true,
	});
	waiting.postMessage({ type: "SKIP_WAITING" });
}
